import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  Package,
  CheckCircle,
  Box,
  Wrench,
  AlertTriangle,
  DollarSign
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import productMasterService from '../../services/productMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ProductMaster = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalProducts: 0,
    activeProducts: 0,
    inactiveProducts: 0,
    goodsType: 0,
    serviceType: 0,
    softwareType: 0,
    lowStockItems: 0,
    totalStockValue: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [productsResponse, statsResponse] = await Promise.all([
        productMasterService.getAllProducts(),
        productMasterService.getProductStats()
      ])
      
      if (productsResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (productsResponse.data || []).map(product => ({
          ...product,
          id: product._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (productsResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No product records found. Create your first product!')
        }
      } else {
        toast.error(productsResponse.message || 'Failed to load products')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading product master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view products')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load product master records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const getProductTypeBadgeClass = (type) => {
    const typeMap = {
      'Goods': 'active',
      'Service': 'warning',
      'Software': 'pending',
      'Hardware': 'active',
      'Consumable': 'pending',
      'Asset': 'inactive',
      'Other': 'inactive'
    }
    return typeMap[type] || 'inactive'
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'Active': 'active',
      'Inactive': 'inactive',
      'Discontinued': 'warning',
      'Out of Stock': 'danger'
    }
    return statusMap[status] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'productCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'productName',
      header: 'Product Name',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'productType',
      header: 'Type',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getProductTypeBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'category',
      header: 'Category',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>
          {getValue() || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'pricing.sellingPrice',
      header: 'Price',
      cell: ({ row }) => {
        const price = row.original.pricing?.sellingPrice || row.original.pricing?.basePrice || 0
        const currency = row.original.pricing?.currency || 'INR'
        return (
          <div style={{ textAlign: 'right', fontWeight: '600' }}>
            {new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(price)}
          </div>
        )
      },
    },
    {
      accessorKey: 'inventory.currentStock',
      header: 'Stock',
      cell: ({ row }) => {
        const trackInventory = row.original.inventory?.trackInventory
        const currentStock = row.original.inventory?.currentStock || 0
        const reorderPoint = row.original.inventory?.reorderPoint || 0
        
        if (!trackInventory) {
          return <div style={{ textAlign: 'center' }}>-</div>
        }
        
        const isLowStock = currentStock <= reorderPoint
        return (
          <div style={{ 
            textAlign: 'center', 
            fontWeight: '600',
            color: isLowStock ? '#dc3545' : '#28a745'
          }}>
            {currentStock}
          </div>
        )
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getStatusBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/procurement/product-master/form')
  }

  const handleEdit = (productData) => {
    navigate('/procurement/product-master/edit', { state: { productData } })
  }

  const handleView = (productData) => {
    navigate('/procurement/product-master/view', { state: { productData } })
  }

  const handleDelete = (productData) => {
    setDeleteItem(productData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting product...')
      const response = await productMasterService.deleteProduct(deleteItem._id)
      if (response.success) {
        toast.success('Product deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete product')
      }
    } catch (error) {
      console.error('Error deleting product:', error)
      if (error.response?.status === 404) {
        toast.error('Product not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this product')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to delete product. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    { label: 'Procurement', path: '/procurement' },
    { label: 'Masters', path: '/procurement?tab=masters' },
    { label: 'Products', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>Products</h1>
          <p>Product catalog and item specifications</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Product
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <Package size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalProducts || 0}</div>
          <div className="metric-title">Total Products</div>
          <div className="metric-subtitle">All catalog items</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeProducts || 0}</div>
          <div className="metric-title">Active Products</div>
          <div className="metric-subtitle">Available to order</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Box size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Goods</span>
            </div>
          </div>
          <div className="metric-value">{stats.goodsType || 0}</div>
          <div className="metric-title">Goods</div>
          <div className="metric-subtitle">Physical items</div>
        </div>

        <div className="metric-card conversion">
          <div className="metric-header">
            <div className="metric-icon">
              <Wrench size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Services</span>
            </div>
          </div>
          <div className="metric-value">{stats.serviceType || 0}</div>
          <div className="metric-title">Services</div>
          <div className="metric-subtitle">Service items</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <AlertTriangle size={16} />
            </div>
            <div className="metric-trend negative">
              <span>Low stock</span>
            </div>
          </div>
          <div className="metric-value">{stats.lowStockItems || 0}</div>
          <div className="metric-title">Low Stock Items</div>
          <div className="metric-subtitle">Needs replenishment</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Value</span>
            </div>
          </div>
          <div className="metric-value">
            {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(stats.totalStockValue || 0)}
          </div>
          <div className="metric-title">Total Stock Value</div>
          <div className="metric-subtitle">Inventory value</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          noCard={true}
          className="product-master-table"
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Product"
        message={`Are you sure you want to delete product "${deleteItem?.productName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default ProductMaster

